import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { 
  BookOpen, ArrowLeft, Video, FileText, MessageCircle, 
  Play, Clock, CheckCircle, Lock, Download, Award, Trophy
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CourseView() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const { user, getAuthHeader } = useAuth();
  const [course, setCourse] = useState(null);
  const [liveClasses, setLiveClasses] = useState([]);
  const [resources, setResources] = useState([]);
  const [tests, setTests] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  const fetchCourseData = async () => {
    try {
      const [courseRes, classesRes, resourcesRes, testsRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`, getAuthHeader()),
        axios.get(`${API}/live-classes`, getAuthHeader()),
        axios.get(`${API}/resources`, getAuthHeader()),
        axios.get(`${API}/tests?course_id=${courseId}`, getAuthHeader()),
        axios.get(`${API}/enrollments/my-courses`, getAuthHeader())
      ]);

      setCourse(courseRes.data);
      setLiveClasses(classesRes.data.filter(c => c.course_id === courseId));
      setResources(resourcesRes.data);
      setTests(testsRes.data);
      
      // Find current enrollment
      const currentEnrollment = enrollmentsRes.data.find(e => e.course_id === courseId);
      setEnrollment(currentEnrollment);
    } catch (error) {
      toast.error('Failed to load course data');
      navigate('/student/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteCourse = async () => {
    setCompleting(true);
    try {
      const response = await axios.post(
        `${API}/courses/${courseId}/complete`,
        {},
        getAuthHeader()
      );
      
      toast.success(`🎉 Congratulations! You earned ${response.data.points_earned} points and a certificate!`, {
        duration: 5000
      });
      
      // Refresh enrollment data
      fetchCourseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete course');
    } finally {
      setCompleting(false);
    }
  };

  const handleJoinClass = (classId) => {
    navigate(`/live-class/${classId}`);
  };

  const handleStartTest = (testId) => {
    navigate(`/test/${testId}`);
  };

  const handleDownloadResource = (resource) => {
    if (resource.access_type === 'paid' && resource.price > 0) {
      toast.info('This is a paid resource. Payment feature coming soon.');
      return;
    }
    if (resource.file_url) {
      window.open(resource.file_url, '_blank');
      toast.success(`Downloading ${resource.title}...`);
    } else {
      toast.error('Resource file not available');
    }
  };

  const isCompleted = enrollment?.status === 'completed';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500">Course not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button
                data-testid="back-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/student/dashboard')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <BookOpen className="w-8 h-8 text-indigo-600" />
              <span className="text-2xl font-extrabold text-slate-900">EduFlow</span>
            </div>
          </div>
        </div>
      </header>

      {/* Course Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-teal-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex-1">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-white/20 mb-4">
                {course.subject}
              </span>
              <h1 data-testid="course-title" className="text-3xl md:text-4xl font-extrabold mb-4">{course.title}</h1>
              <p className="text-lg text-white/90 mb-4 max-w-2xl">{course.description}</p>
              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  {course.duration}
                </div>
                <div className="flex items-center">
                  <BookOpen className="w-4 h-4 mr-2" />
                  {liveClasses.length} Live Classes
                </div>
                <div className="flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  {resources.length} Resources
                </div>
                <div className="flex items-center">
                  <Award className="w-4 h-4 mr-2" />
                  {tests.length} Tests
                </div>
              </div>
              {course.teacher_name && (
                <p className="mt-4 text-sm text-white/80">Instructor: {course.teacher_name}</p>
              )}
            </div>
            <div className="mt-6 md:mt-0">
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 p-6 text-center">
                {isCompleted ? (
                  <>
                    <Trophy className="w-12 h-12 mx-auto mb-3 text-amber-300" />
                    <p className="text-lg font-semibold">Course Completed!</p>
                    <p className="text-sm text-white/80 mt-1">Certificate earned</p>
                    <Button 
                      onClick={() => navigate('/student/profile')}
                      className="mt-3 bg-white text-indigo-600 hover:bg-white/90 rounded-full"
                      size="sm"
                    >
                      View Certificate
                    </Button>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                    <p className="text-lg font-semibold">You're Enrolled!</p>
                    <p className="text-sm text-white/80 mt-1">Start learning now</p>
                    <Button 
                      data-testid="complete-course-btn"
                      onClick={handleCompleteCourse}
                      disabled={completing}
                      className="mt-3 bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                      size="sm"
                    >
                      {completing ? 'Processing...' : 'Mark Complete & Earn Certificate'}
                    </Button>
                  </>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 p-1 gap-1 mb-8">
            <TabsTrigger 
              value="overview" 
              data-testid="overview-tab"
              className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger 
              value="classes" 
              data-testid="classes-tab"
              className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700"
            >
              Live Classes
            </TabsTrigger>
            <TabsTrigger 
              value="resources" 
              data-testid="resources-tab"
              className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700"
            >
              Resources
            </TabsTrigger>
            <TabsTrigger 
              value="tests" 
              data-testid="tests-tab"
              className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700"
            >
              Tests & Quizzes
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Course Content */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">What You'll Learn</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-600">Comprehensive course material</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-600">Live interactive sessions</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-600">Practice tests and quizzes</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-600">Doubt clearing sessions</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-600">Downloadable resources</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-600">Certificate on completion</p>
                    </div>
                  </div>
                </Card>

                {/* Upcoming Classes Preview */}
                <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-slate-900">Upcoming Classes</h2>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setActiveTab('classes')}
                      className="rounded-full"
                    >
                      View All
                    </Button>
                  </div>
                  {liveClasses.length === 0 ? (
                    <p className="text-slate-500 text-center py-6">No upcoming classes scheduled</p>
                  ) : (
                    <div className="space-y-3">
                      {liveClasses.slice(0, 3).map((liveClass) => (
                        <div 
                          key={liveClass.id}
                          data-testid={`upcoming-class-${liveClass.id}`}
                          className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-indigo-200 transition-colors"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                              <Video className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-900">{liveClass.title}</h3>
                              <p className="text-sm text-slate-500">
                                {new Date(liveClass.scheduled_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            onClick={() => handleJoinClass(liveClass.id)}
                            className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Join
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Progress Card */}
                <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Your Progress</h3>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Course Completion</span>
                      <span className="font-semibold text-slate-900">0%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 rounded-full">
                      <div className="h-full bg-indigo-600 rounded-full" style={{ width: '0%' }}></div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Classes Attended</span>
                      <span className="font-medium text-slate-900">0/{liveClasses.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Tests Completed</span>
                      <span className="font-medium text-slate-900">0/{tests.length}</span>
                    </div>
                  </div>
                </Card>

                {/* Quick Actions */}
                <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <Button 
                      data-testid="ask-doubt-btn"
                      onClick={() => navigate('/student/dashboard')}
                      variant="outline" 
                      className="w-full justify-start rounded-lg"
                    >
                      <MessageCircle className="w-4 h-4 mr-3" />
                      Ask a Doubt
                    </Button>
                    <Button 
                      onClick={() => setActiveTab('resources')}
                      variant="outline" 
                      className="w-full justify-start rounded-lg"
                    >
                      <Download className="w-4 h-4 mr-3" />
                      Download Materials
                    </Button>
                    <Button 
                      onClick={() => setActiveTab('tests')}
                      variant="outline" 
                      className="w-full justify-start rounded-lg"
                    >
                      <Award className="w-4 h-4 mr-3" />
                      Take a Test
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Live Classes Tab */}
          <TabsContent value="classes">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Live Classes</h2>
              {liveClasses.length === 0 ? (
                <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-12 text-center">
                  <Video className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No live classes scheduled for this course yet</p>
                </Card>
              ) : (
                liveClasses.map((liveClass) => (
                  <Card
                    key={liveClass.id}
                    data-testid={`class-card-${liveClass.id}`}
                    className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 hover:shadow-md transition-all"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <Video className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-bold text-slate-900">{liveClass.title}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              liveClass.status === 'live' ? 'bg-green-100 text-green-700' :
                              liveClass.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {liveClass.status}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mb-3">{liveClass.description}</p>
                          <div className="flex items-center space-x-4 text-sm text-slate-500">
                            <span>📅 {new Date(liveClass.scheduled_at).toLocaleDateString()}</span>
                            <span>⏰ {new Date(liveClass.scheduled_at).toLocaleTimeString()}</span>
                            <span>⏱️ {liveClass.duration_minutes} min</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 md:mt-0 md:ml-6">
                        <Button
                          data-testid={`join-class-${liveClass.id}`}
                          onClick={() => handleJoinClass(liveClass.id)}
                          className="rounded-full bg-indigo-600 hover:bg-indigo-700 px-6"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Join Class
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Course Resources</h2>
              {resources.length === 0 ? (
                <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-12 text-center">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No resources available for this course yet</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {resources.map((resource) => (
                    <Card
                      key={resource.id}
                      data-testid={`resource-card-${resource.id}`}
                      className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-teal-600" />
                        </div>
                        {resource.access_type === 'free' ? (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-700">
                            Free
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 flex items-center">
                            <Lock className="w-3 h-3 mr-1" />
                            ₹{resource.price}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">{resource.title}</h3>
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">{resource.description}</p>
                      <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                        <span className="text-xs text-slate-500 uppercase font-medium">{resource.type}</span>
                        <Button
                          data-testid={`download-resource-${resource.id}`}
                          onClick={() => handleDownloadResource(resource)}
                          size="sm"
                          className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Tests Tab */}
          <TabsContent value="tests">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Tests & Quizzes</h2>
              {tests.length === 0 ? (
                <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-12 text-center">
                  <Award className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No tests available for this course yet</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tests.map((test) => (
                    <Card
                      key={test.id}
                      data-testid={`test-card-${test.id}`}
                      className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Award className="w-6 h-6 text-amber-600" />
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                          {test.questions?.length || 0} Questions
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">{test.title}</h3>
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">{test.description}</p>
                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex justify-between text-slate-600">
                          <span>Duration:</span>
                          <span className="font-medium">{test.duration_minutes} min</span>
                        </div>
                        <div className="flex justify-between text-slate-600">
                          <span>Total Marks:</span>
                          <span className="font-medium">{test.total_marks}</span>
                        </div>
                      </div>
                      <Button
                        data-testid={`start-test-${test.id}`}
                        onClick={() => handleStartTest(test.id)}
                        className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700"
                      >
                        Start Test
                      </Button>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
